import React, { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Defined outside the component so Quill doesn't re-initialise (and lose focus) on every render
const MODULES = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["clean"],
  ],
};

const FORMATS = ["bold", "italic", "underline", "link", "list", "bullet"];

const TOOLBAR_HEIGHT = 36; // px - fixed, so the box height is exact
const BOX_CHROME = TOOLBAR_HEIGHT + 2; // toolbar + top/bottom border

const EDITOR_CSS = `
.quotation-editor.quotation-editor {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border: 1px solid #add8e6;
  border-radius: 4px;
  text-align: left;
}
.quotation-editor.quotation-editor .quill {
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.quotation-editor.quotation-editor .ql-toolbar.ql-snow {
  flex: 0 0 ${TOOLBAR_HEIGHT}px;
  height: ${TOOLBAR_HEIGHT}px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  border: 0;
  border-bottom: 1px solid #add8e6;
  padding: 0 6px;
  background: #fff;
  text-align: left;
}
.quotation-editor.quotation-editor .ql-container.ql-snow {
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  overflow: hidden;
  border: 0;
  background: #fff;
  font-family: inherit;
  font-size: 13px;
  height: auto;
}
.quotation-editor.quotation-editor .ql-editor {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  height: auto;
  min-height: 0 !important;
  max-height: none !important;
  overflow-x: hidden !important;
  overflow-y: auto !important;
  padding: 8px 10px;
  font-size: 13px;
  line-height: 1.5;
  text-align: left;
}
.quotation-editor.quotation-editor .ql-editor.ql-blank::before {
  left: 10px;
  right: 10px;
  font-style: normal;
  color: #9ca3af;
}
.quotation-editor.quotation-editor .ql-editor p,
.quotation-editor.quotation-editor .ql-editor ol,
.quotation-editor.quotation-editor .ql-editor ul {
  margin: 0;
}
/* The quotation table has a global rule for <p> (single line + "..." ellipsis).
   Reset it here so the full text is visible and wraps normally. */
.quotation-editor.quotation-editor .ql-editor p,
.quotation-editor.quotation-editor .ql-editor li {
  white-space: pre-wrap !important;
  overflow: visible !important;
  text-overflow: clip !important;
  max-width: none !important;
  width: auto !important;
  -webkit-line-clamp: unset !important;
  overflow-wrap: anywhere;
}
.quotation-editor.quotation-editor .ql-editor p {
  display: block !important;
}
`;

const escapeHtml = (str) =>
  str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Old quotations have plain-text values - keep their line breaks inside Quill
const toEditorHtml = (val) => {
  if (!val) return "";
  if (/<\/?[a-z][\s\S]*?>/i.test(val)) return val; // already HTML
  return val
    .split(/\r?\n/)
    .map((line) => `<p>${line ? escapeHtml(line) : "<br>"}</p>`)
    .join("");
};


const QuotationEditor = ({
  value,
  onChange,
  onBlur,
  placeholder,
  minHeight = 80,
  maxHeight,
}) => {
  const [inner, setInner] = useState(toEditorHtml(value));
  const lastEmitted = useRef(value ?? "");

  // Sync when the form changes the value from outside
  // (reset(), setValue() - e.g. picking a product fills its description, Cancel button clears it)
  useEffect(() => {
    const incoming = value ?? "";
    if (incoming !== lastEmitted.current) {
      lastEmitted.current = incoming;
      setInner(toEditorHtml(incoming));
    }
  }, [value]);

  const handleChange = (html, _delta, source, editor) => {
    if (source !== "user") return;
    setInner(html);
    // Quill returns "<p><br></p>" when empty - send "" so empty checks keep working
    const isEmpty = editor.getText().trim().length === 0 && !/<img/i.test(html);
    const out = isEmpty ? "" : html;
    lastEmitted.current = out;
    onChange?.(out);
  };

  const totalHeight = (maxHeight ?? minHeight) + BOX_CHROME;

  return (
    <>
      <style>{EDITOR_CSS}</style>
      <div className="quotation-editor" style={{ height: totalHeight }}>
        <ReactQuill
          theme="snow"
          value={inner}
          onChange={handleChange}
          onBlur={() => onBlur?.()}
          placeholder={placeholder}
          modules={MODULES}
          formats={FORMATS}
        />
      </div>
    </>
  );
};

export default QuotationEditor;